/* The counterparty card's address form — create and edit, one modal (`AddressesTab.js` opens it).

     POST  excalibur/counterparty-addresses/        {counterparty, …every field}
     PATCH excalibur/counterparty-addresses/:id/    {…only the fields that changed}

   Both answer with the address in the list's shape; the caller re-reads the list anyway. The API
   silently ignores an unknown key (no 400), so only `FIELDS` is ever sent, and `counterparty` only
   on create. A new primary address un-marks the previous one server-side.

   The region is NOT a field of the address — it only narrows the locality picker (the counterparty
   list's own filter does the same: `localities?county=<one id>`), and is seeded from the locality's
   own county on edit. Changing it clears a locality from another region.

   Coordinates are optional: `LeafletMap` in its `allowEmpty` mode has no pin until the map is
   clicked; the pin can then be dragged, and "Clear" removes it (`location: null`). Not
   `LocationPickerField`: that one geocodes into a point's `address` field, which an address does
   not have, and is styled by the points stylesheet.

   Save is live on a pristine NEW address (spec's `canSave = item.id ? dirty : true`) — the API, not
   the form, decides what is missing: its 400 marks each refused field, "fill this in" for an empty
   required one and the card form's phrase otherwise, and focuses the first. */

import React, { useMemo, useState } from "react";
import { Button, Checkbox, Col, Flex, Form, Input, Modal, Row, Typography } from "antd";
import { AimOutlined, CloseOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { COUNTERPARTIES_PAGE, TRACKING, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, parseErrors } from "../../util/action";
import { translate } from "../../util/util";
import LeafletMap from "../points/ext/LeafletMap";

const URL = "excalibur/counterparty-addresses/";
/* Live values only — the endpoint lists no deleted one. A deleted type already on the address shows
   by name (`seed`) and is not re-sent unless changed. */
const TYPE_QUERY = { kind: "address_type" };
const FIELDS = ["address_type", "locality", "street", "building_type", "location", "is_primary", "comment"];
const REQUIRED = ["locality", "street"];
const fieldId = key => `cp-addr-${key}`;
/* No coordinates anywhere yet: open on Ukraine rather than the ocean at [0,0]. */
const FALLBACK_VIEW = { center: [49.0, 31.4], zoom: 6 };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/* The form state of an address row (or of a blank one). Pickers hold full objects, so a value
   chosen on page 7 still labels itself. */
const toForm = row => ({
  address_type: row?.address_type || null,
  county: row?.locality?.county || null,
  locality: row?.locality || null,
  street: row?.street || "",
  building_type: row?.building_type || "",
  location: row?.location || null,
  is_primary: !!row?.is_primary,
  comment: row?.comment || "",
});

/* What the API takes. */
const toPayload = form => ({
  address_type: idOf(form.address_type) ?? null,
  locality: idOf(form.locality) ?? null,
  street: form.street.trim(),
  building_type: form.building_type.trim(),
  location: form.location ? { latitude: form.location.latitude, longitude: form.location.longitude } : null,
  is_primary: form.is_primary,
  comment: form.comment.trim(),
});

const round = value => Math.round(value * 1e6) / 1e6;

/* A phone gets a shorter map, so the fields around it stay in reach (antd's `sm` breakpoint). */
const MAP_HEIGHT = () => (typeof window !== "undefined" && window.innerWidth < 576 ? 220 : 280);

export default function AddressModal({ row, counterpartyId, lang, mapView, onClose, onSaved }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const isNew = !row?.id;
  const initial = useMemo(() => toForm(row), [row]);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const baseline = useMemo(() => JSON.stringify(toPayload(initial)), [initial]);
  const dirty = JSON.stringify(toPayload(form)) !== baseline;
  const canSave = isNew ? true : dirty;

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: null } : prev));
  };

  const pickCounty = value => {
    setForm(prev => {
      const sameRegion = prev.locality && String(idOf(prev.locality.county)) === String(idOf(value));
      return { ...prev, county: value, locality: sameRegion ? prev.locality : null };
    });
  };

  const submit = () => {
    const payload = toPayload(form);
    const was = JSON.parse(baseline);
    /* Edit sends the changed keys only; create sends every field plus its counterparty. */
    const post = isNew
      ? { counterparty: counterpartyId, ...payload }
      : Object.fromEntries(FIELDS.filter(k => JSON.stringify(payload[k]) !== JSON.stringify(was[k])).map(k => [k, payload[k]]));
    setBusy(true);
    setErrors({});
    call({ method: isNew ? "POST" : "PATCH", url: isNew ? URL : `${URL}${row.id}/`, post }, response => {
      setBusy(false);
      if (response?.id) {
        Alert.success(t("ad_saved_msg"), { position: "top-right" });
        onSaved();
        return;
      }
      /* A 400 is the raw `Response`. A 5xx / network failure has already been toasted. */
      if (typeof response?.json === "function") {
        parseErrors(response).then(parsed => {
          const keys = Object.keys(parsed.fields);
          const known = keys.filter(key => FIELDS.includes(key));
          if (known.length) {
            setErrors(Object.fromEntries(known.map(key => [key, REQUIRED.includes(key) && !payload[key] ? "ad_required" : "ad_field_invalid"])));
            setTimeout(() => document.getElementById(fieldId(known[0]))?.focus(), 0);
          }
          if (!known.length || known.length < keys.length) Alert.warning(t("edit_error_msg"), { position: "top-right" });
        });
        return;
      }
      if (response?.detail) Alert.warning(t("edit_error_msg"), { position: "top-right" });
    });
  };

  /* `labelKey` where the field's label is not its own `ad_f_*` (the comment shares the documents'). */
  const item = (key, labelKey) => ({
    label: t(labelKey || `ad_f_${key === "address_type" ? "type" : key === "building_type" ? "building" : key === "is_primary" ? "primary" : key}`),
    htmlFor: fieldId(key),
    required: REQUIRED.includes(key),
    ...(errors[key] ? { validateStatus: "error", help: t(errors[key]) } : {}),
  });

  const loc = form.location;
  const view = loc ? { center: [loc.latitude, loc.longitude], zoom: 16 } : mapView || FALLBACK_VIEW;

  return (
    <Modal
      open
      title={t(isNew ? "ad_modal_new" : "ad_modal_edit")}
      okText={t("edit_save")}
      okButtonProps={{ disabled: !canSave, loading: busy }}
      cancelText={translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
      onOk={submit}
      onCancel={onClose}
      width={720}
      /* Near the top, so the scrolling body (`.cp-admodal`) leaves Save on screen down to a phone. */
      style={{ top: 24 }}
      rootClassName="cp-admodal">
      <Form component={false} layout="vertical" colon={false}>
        <Flex vertical className="dp-form cp-admodal__form">
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item {...item("address_type")}>
                <EntitySelect
                  id={fieldId("address_type")}
                  entity="address_type"
                  extraQuery={TYPE_QUERY}
                  value={form.address_type?.id}
                  seed={form.address_type}
                  placeholder={t("ad_f_type")}
                  onChange={value => set("address_type", value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("building_type")}>
                <Input id={fieldId("building_type")} maxLength={128} value={form.building_type} onChange={e => set("building_type", e.target.value)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={t("ad_f_county")} htmlFor={fieldId("county")}>
                <EntitySelect
                  id={fieldId("county")}
                  entity="county"
                  value={form.county?.id}
                  seed={form.county}
                  placeholder={t("ad_f_county")}
                  onChange={pickCounty}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item {...item("locality")}>
                <EntitySelect
                  id={fieldId("locality")}
                  entity="locality"
                  extraQuery={form.county ? { county: idOf(form.county) } : undefined}
                  value={form.locality?.id}
                  seed={form.locality}
                  placeholder={t("ad_f_locality")}
                  onChange={value => set("locality", value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item {...item("street")}>
            <Input id={fieldId("street")} maxLength={256} value={form.street} onChange={e => set("street", e.target.value)} />
          </Form.Item>
          <Form.Item {...item("location")}>
            <Flex vertical gap={8}>
              <Flex align="center" justify="space-between" gap="small" wrap>
                <Typography.Text type="secondary" className="cp-admodal__coords">
                  <AimOutlined /> {loc ? `${round(loc.latitude)}, ${round(loc.longitude)}` : t("ad_map_hint")}
                </Typography.Text>
                {loc ? (
                  <Button size="small" icon={<CloseOutlined />} onClick={() => set("location", null)}>
                    {t("ad_clear_location")}
                  </Button>
                ) : null}
              </Flex>
              {/* `id` for the 400 focus: the map itself is not a form control. */}
              <div id={fieldId("location")} tabIndex={-1} className="cp-admodal__map">
                <LeafletMap
                  failedText={translate(TRACKING, "map_unavailable", lang)}
                  allowEmpty
                  editable
                  lat={loc ? loc.latitude : null}
                  lng={loc ? loc.longitude : null}
                  emptyCenter={view.center}
                  zoom={view.zoom}
                  onChange={({ lat, lng }) => set("location", { latitude: round(lat), longitude: round(lng) })}
                  height={MAP_HEIGHT()}
                />
              </div>
            </Flex>
          </Form.Item>
          <Form.Item {...item("is_primary")} label={null}>
            <Checkbox id={fieldId("is_primary")} checked={form.is_primary} onChange={e => set("is_primary", e.target.checked)}>
              {t("ad_f_primary")}
            </Checkbox>
          </Form.Item>
          <Form.Item {...item("comment", "doc_comment")}>
            <Input.TextArea id={fieldId("comment")} rows={2} value={form.comment} onChange={e => set("comment", e.target.value)} />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
}
